import React, { useEffect, useRef, useState } from 'react';
import TextBox from 'devextreme-react/text-box';
import SelectBox from 'devextreme-react/select-box';
import DateBox from 'devextreme-react/date-box';
import NumberBox from 'devextreme-react/number-box';
import TextArea from 'devextreme-react/text-area';
import { CheckBox, Switch, Validator } from 'devextreme-react';
import { RequiredRule } from 'devextreme-react/data-grid';
import moment from 'moment';
import { useAppSelector } from '../../../contexts/hooks';
import { formatMessage } from 'devextreme/localization';
import { SystemFields } from '../../../types/types.config';

export const CustomFormControl = ({ data, onValueChange, isEditing, value, valueDepency, defaultValue }: { data: SystemFields; onValueChange: (e: any) => void; isEditing: boolean; value?: any; defaultValue?: any; valueDepency?: any }) => {
  const cmb = useAppSelector((x) => x.configuration.Data?.ComboBox) || [];
  const dateFormat = useAppSelector((x) => x.configuration.Data?.Config_General.find((c) => c.Cgn_Nombre === 'DateTimeClientFormat')?.Cgn_Resultado);
  const refSelectBox = useRef<SelectBox>(null);
  const [localCmbData, setLocalCmbData] = useState<any[]>([]);

  // Manejar datos del combo
  useEffect(() => {
    const isSelectOrLookup = data.TipoCampo.Tcm_Equivalencia === 'dxSelectBox' || data.TipoCampo.Tcm_Equivalencia === 'dxLookup';
    if (!isSelectOrLookup) return;

    const comboSource = cmb.find((x) => x.Name === data.TablasComboBox.Tbc_NombreTabla)?.Data;

    if (!comboSource || !Array.isArray(comboSource)) {
      setLocalCmbData([]);
      return;
    }

    let newCmbData = [...comboSource];

    // Si tiene dependencia, aplicamos el filtro
    if (data.Dfc_Dependency && valueDepency != null) {
      const depValue = typeof valueDepency === 'string' ? parseInt(valueDepency, 10) : valueDepency;

      newCmbData = newCmbData.filter((item) => {
        const itemValue = item[data.Dfc_DependencyField];
        const match = itemValue === depValue;

        return match;
      });
    }

    setLocalCmbData(newCmbData);
  }, [cmb, data.TablasComboBox?.Tbc_NombreTabla, data.Dfc_Dependency, data.Dfc_DependencyField, valueDepency]);

  // Limpieza de valor solo cuando cambia la dependencia
  useEffect(() => {
    if (data.Dfc_Dependency && data.TipoCampo.Tcm_Equivalencia === 'dxSelectBox' && valueDepency === null && value != null) {
      onValueChange(null);
    }
  }, [valueDepency]);

  switch (data.TipoCampo.Tcm_Equivalencia) {
    case 'dxSelectBox':
    case 'dxLookup': {
      const valueExpr = data.TablasComboBox.Tbc_CampoId;
      const displayExpr = data.TablasComboBox.Tbc_CampoVisible;

      return (
        <SelectBox ref={refSelectBox} defaultValue={defaultValue != null ? parseInt(defaultValue, 10) : undefined} stylingMode='outlined' labelMode='static' searchEnabled value={value != null ? parseInt(value, 10) : undefined} items={localCmbData} valueExpr={valueExpr} displayExpr={displayExpr} label={data.Dfc_Label} onValueChange={onValueChange} readOnly={!isEditing || !data.Dfc_Editable}>
          {data.Dfc_Requerido && (
            <Validator>
              <RequiredRule message={`${data.Dfc_Label} ${formatMessage('requiredField')}`} />
            </Validator>
          )}
        </SelectBox>
      );
    }

    case 'dxDateBox':
      return (
        <DateBox stylingMode='outlined' defaultValue={defaultValue} maskInvalidMessage={formatMessage('invalidFormat')} placeholder={moment().format(dateFormat?.toUpperCase() || '')} useMaskBehavior labelMode='static' type='date' displayFormat={dateFormat} value={value} invalidDateMessage={formatMessage('invalidFormat')} label={data.Dfc_Label} onValueChange={onValueChange} readOnly={!isEditing || !data.Dfc_Editable} disabled={!isEditing || !data.Dfc_Editable}>
          {data.Dfc_Requerido && (
            <Validator>
              <RequiredRule message={`${data.Dfc_Label} ${formatMessage('requiredField')}`} />
            </Validator>
          )}
        </DateBox>
      );

    case 'dxNumberBox':
      return (
        <NumberBox stylingMode='outlined' defaultValue={defaultValue}  format={data.Dfc_Formato || undefined} labelMode='static' value={value} validationError={null} maskInvalidMessage={formatMessage('invalidFormat')} label={data.Dfc_Label} onValueChange={onValueChange} readOnly={!isEditing || !data.Dfc_Editable} mask={data.Dfc_Mascara || ''}>
          {data.Dfc_Requerido && (
            <Validator>
              <RequiredRule message={`${data.Dfc_Label} ${formatMessage('requiredField')}`} />
            </Validator>
          )}
        </NumberBox>
      );

    case 'dxCheckBox':
      return (
        <>
          <div className='dx-field-label'>{data.Dfc_Label}</div>
          <CheckBox defaultValue={defaultValue || false} stylingMode='outlined' value={value} validationError={null} onValueChange={onValueChange} readOnly={!isEditing || !data.Dfc_Editable} />
        </>
      );

    case 'dxSwitch':
      return (
        <>
          <div className='dx-field-label'>{data.Dfc_Label}</div>
          <Switch stylingMode='outlined' value={value} defaultValue={defaultValue || false} validationError={null} onValueChange={onValueChange} readOnly={!isEditing || !data.Dfc_Editable} />
        </>
      );

    case 'dxTextArea':
      return (
        <TextArea stylingMode='outlined' defaultValue={defaultValue} maxLength={data.Dfc_Maximo ? parseInt(data.Dfc_Maximo, 10) : 10000} width='100%' height={100} labelMode='static' value={value} validationError={null} maskInvalidMessage={formatMessage('invalidFormat')} label={data.Dfc_Label} onValueChange={onValueChange} readOnly={!isEditing || !data.Dfc_Editable} mask={data.Dfc_Mascara || ''}>
          {data.Dfc_Requerido && (
            <Validator>
              <RequiredRule message={`${data.Dfc_Label} ${formatMessage('requiredField')}`} />
            </Validator>
          )}
        </TextArea>
      );

    default:
      return (
        <TextBox stylingMode='outlined' defaultValue={defaultValue} maxLength={data.Dfc_Maximo ? parseInt(data.Dfc_Maximo, 10) : 5000} labelMode='static' value={value} validationError={null} maskInvalidMessage={formatMessage('invalidFormat')} label={data.Dfc_Label} onValueChange={onValueChange} readOnly={!isEditing} mask={data.Dfc_Mascara || ''}>
          {data.Dfc_Requerido && (
            <Validator>
              <RequiredRule message={`${data.Dfc_Label} ${formatMessage('requiredField')}`} />
            </Validator>
          )}
        </TextBox>
      );
  }
};

export default CustomFormControl;
